/**
 * HustleMeetMatchmakerSalesPage.jsx — /matchmaker
 *
 * Dedicated sales page for the AI Matchmaker sub-product. Fully public
 * — no login required. Every string is translated via
 * `hm(t, "matchmaker.<key>")`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Sparkles, Brain, Users, MapPin, ArrowRight, Shield, Heart,
  MessageSquare, Repeat, Clock, CheckCircle2, Lock, Zap, Star,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { hm } from "@/lib/hmCopy";

/* ── Long-form sales content (English source-of-truth). Kept outside
 *  the component so the arrays aren't rebuilt on every render. Will
 *  hoist into hmCopy.js on the next translation pass. */
const MM_HERO_IMG = "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1600&q=80";
const MM_IMG_TOAST   = "https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=1600&q=80";
const MM_IMG_SCIENCE = "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1600&q=80";
const MM_IMG_CANDLES = "https://images.unsplash.com/photo-1541943181603-d8fe267a5dcf?auto=format&fit=crop&w=1600&q=80";

const MM_INTRO_PARAS = [
  "Some of the best nights of your life have started with strangers you didn't choose. A sailing trip where nobody knew each other on day one. A wedding table where six unrelated people ended up crying-laughing by main course. A hostel dinner in a city you'll probably never revisit. There's a specific magic to conversations that begin without pretence — where nobody's trying to impress, nobody knows anyone's job title, and by dessert everyone's swapping numbers.",
  "Matchmaker recreates that experience deliberately. Every fortnight, six verified strangers sit down together at a partnered café, restaurant, wine bar or cocktail lounge in your city. Every venue has signed up specifically to host Matchmaker nights — quieter room, curated menu, no walk-in interruptions. Our algorithm picked the six seats from thousands. The table has no host, no meeting agenda, no getting-to-know-you games. Just six people who — based on personality overlap, life-stage alignment, curiosity match, and geographic proximity — should genuinely enjoy each other's company.",
  "You take a ten-minute quiz. We do the rest. The venue is confirmed. The table is booked. Last names, workplaces and social handles stay confidential. Only your first name and a two-line intro reach your table until you sit down. The conversation starts on its own.",
];

const MM_SECTIONS = [
  {
    title: "The Best Nights Start With Strangers Who Feel Like Old Friends By Dessert",
    paragraphs: [
      "There's a specific kind of night — you know the one — where the timing of who sat where made the whole thing work. Everyone leaves with new numbers in their phone and no idea how it just clicked. Matchmaker is engineered around reproducing that specific magic. Not through gimmicks, not through icebreakers, not through name badges. Through curation.",
      "When six people whose Big-5 personality profiles complement each other, whose life stages naturally align, and whose curiosity radars point in similar directions all sit at the same table — those six people, more often than not, have exactly that night. We can't guarantee it every time. But we can move the odds massively in your favour.",
    ],
  },
  {
    title: "How Matchmaker Picks Your Table",
    paragraphs: [
      "You complete a ten-minute onboarding quiz. It's genuinely ten minutes — you can do it on your phone while your coffee cools. The quiz uses a shortened Big-Five personality inventory (openness, conscientiousness, extraversion, agreeableness, neuroticism) plus five life-stage questions and a small values grid.",
      "From that we build your matchmaker profile. Every fortnight, when we plan the next batch of tables, our algorithm sifts through the eligible pool in your city and finds the five other people whose profiles complement yours best — deliberately, not by simple similarity. A great table needs one conversational catalyst, one deep listener, one dry-humoured provocateur, one broad-interest bridge, one curious question-asker, and one storyteller. We seat the mix.",
    ],
  },
  {
    title: "The Science Behind The Seating",
    paragraphs: [
      "The Big Five is the single most-validated personality framework in psychology. It predicts long-term compatibility in friendship groups, in workplace teams, and in dinner-party enjoyment more reliably than any other model. Matchmaker doesn't use it to pair \u201Csimilar\u201D people — that produces echo chambers where conversations die within thirty minutes.",
      "Instead, we use it to build tables with complementary friction: enough openness overlap that curiosity flows, enough extraversion balance that no one dominates, enough agreeableness distribution that debate stays warm. Add in shared life-stage (roughly matched decade of life, similar work or family stage) and shared postcode (nobody's driving ninety minutes) and you've got the exact seating recipe a great human matchmaker would put together over an afternoon of thought — except we do it in seven milliseconds, six people at a time, in every city we operate in.",
    ],
  },
  {
    title: "Partnered Cafés and Restaurants Do the Setting",
    paragraphs: [
      "Matchmaker doesn't run its nights in random venues. Every table is placed at a partnered café, restaurant, wine bar or cocktail lounge that has signed up specifically to host Matchmaker meets. Partners understand the format — anonymous, first-name-only guests, a curated menu, a quieter section of the room, the seating chart pre-arranged by us — and they get behind it. The result is a warmer arrival, a faster kitchen, and a room that already knows what kind of night it's helping to run.",
      "Partners include intimate wine bars, neighbourhood bistros, tasting-menu restaurants, speciality cafés for morning Matchmaker breakfasts, and cocktail lounges for after-work groups. Every partner venue is audited for food safety, licensing, and its ability to hold a private table without treating the group as an afterthought. Our partner venue list grows every week — the live count is shown at the top of this page — so tables land at a variety of settings across each Matchmaker city we operate in.",
    ],
  },
  {
    title: "Anonymous, Private, Confidential — By Design",
    paragraphs: [
      "Last names are not shared. Company names are not shared. Social handles are not shared. Nothing about your Matchmaker profile leaves the seating chart. This is deliberate. It stops LinkedIn creeping, it prevents pre-emptive social filtering (dropping the group because you Googled someone), and it forces the first conversation to be about who someone is, not what they do.",
      "You get one photograph the day before (so you can find your table on arrival), a first name, and a two-line intro. That's it. Members never see other members' full names on the platform, and the venue only ever gets a table count and a first-name seating list. This anonymity is the biggest single reason Matchmaker nights work. Everyone shows up as themselves, not their resume — and everything they share at the table stays at the table.",
    ],
  },
  {
    title: "A Different Table, Every Time",
    paragraphs: [
      "You never sit with the same five people twice. Even if you loved everyone at your first table (and you probably will love at least two), your second Matchmaker night is with a completely fresh group. This isn't a limitation — it's the point.",
      "Different combinations produce different nights. Someone you meet at your third dinner might become a lifelong friend. Someone at your seventh might introduce you to your business partner. The rotation is what keeps Matchmaker fresh, and it's what removes the pressure of any single dinner having to be perfect. Bad night? Doesn't matter. There's another one in two weeks with an entirely new table.",
    ],
  },
  {
    title: "Big-5 Personality × Life Stage × Location = Your Perfect Six",
    paragraphs: [
      "The three inputs that matter most are personality, life stage, and location. Personality determines whether a table has energy or drags. Life stage determines whether people relate to each other's daily reality. Location determines whether anyone actually shows up. Get one wrong and the night suffers.",
      "Matchmaker rebuilds the seating pool from scratch every fortnight, so no factor is stale. A new member who joined last Tuesday gets fully mixed into the next table. Someone whose life stage changed (new baby, new city, new job) gets updated automatically as soon as they refresh their profile. The tables are always fresh, always locally curated, always six of a kind.",
    ],
  },
  {
    title: "Why It Works When Speed-Dating Doesn't",
    paragraphs: [
      "Speed-dating puts you in front of twenty strangers for four minutes each, forces a decision, and makes everyone perform. Matchmaker gives you three hours with five people and no decision to make. There's no pressure to click. There's no scoring. There's no swiping the next morning.",
      "If you love a night, you can suggest continuing at another dinner as a group — Matchmaker makes that easy. If you don't, you never see them again. The pressure is removed by design. And that's exactly why real friendships form: friendships need a slow burn, not a spark test.",
    ],
  },
  {
    title: "Verified, But Never Exposed",
    paragraphs: [
      "Every Matchmaker member has passed the same ID verification as every other HustleMeetups member. Every member has a reliability score based on RSVP consistency and turn-up rate. Every member has agreed to the Matchmaker code (no dating, no pitching, no LinkedIn adds during dinner).",
      "If a member misbehaves on a night, one report triggers a moderation review the same evening. The system is quiet, robust, and disproportionately effective. Trust is designed into the seating chart from the first quiz question.",
    ],
  },
  {
    title: "The Curated Conversation Kit",
    paragraphs: [
      "Every Matchmaker dinner ships with a small conversation kit — three curated prompts on a card at the centre of the table. You don't have to use them. Most tables never do. But they're there as a fallback if a natural lull hits.",
      "The prompts are chosen based on the table's Big-Five profile — some tables get abstract prompts, others get playful ones, others get thoughtful reflection ones. This is a small touch that matters more than it should. If a table has one really shy member, the prompts save them. If a table has one really talkative member, the prompts redirect. If the table is already flying, the card gets ignored — and that's fine.",
    ],
  },
  {
    title: "One Quiz, Countless Nights",
    paragraphs: [
      "You take the onboarding quiz once. From that moment onward, every fortnight, you get a Matchmaker invitation the moment a suitable table opens in your city. RSVP yes and you're seated. RSVP no and the seat rolls forward to the next member in the pool. There's no obligation to attend every event.",
      "Some members go weekly. Some go quarterly. Some go three times and take a break. Whatever cadence suits your life. As soon as you're ready for another night, tap \u201Csend me an invitation\u201D and Matchmaker reactivates. One quiz. Countless nights. Six new faces every time. And the possibility — every single time — of finding one of the best rooms of people you'll ever meet.",
    ],
  },
  {
    title: "A Typical Matchmaker Dinner, Minute by Minute",
    paragraphs: [
      "Six-forty-five on a Thursday. You arrive at a mid-size restaurant in a suburb you've been meaning to try. A host at the door checks your first name against the seating chart and walks you to a round table set for six. Two people are already there — a first name and a photograph you saw yesterday. Small waves. You sit down. Within four minutes the other three arrive.",
      "The waiter drops the first shared plate at seven o'clock sharp. Someone at the table starts asking a question. Someone else picks it up. By seven-thirty, three parallel conversations are happening at once and the table is loud in the way you want dinner tables to be. The conversation kit sits in the middle of the table untouched. Nobody needs it.",
      "Around nine, dessert arrives and the pace shifts. Numbers get exchanged. Two people at the table realise they live within four blocks of each other and haven't ever met. Someone asks whether the group should do another dinner. Matchmaker takes care of that too — a single tap suggests the entire table for a follow-up Circle, and everyone can vote yes / no in the morning. Nobody had to organise anything. Just a table, a menu, and five people who now know your first name.",
    ],
  },
  {
    title: "What Members Say After Their First Dinner",
    paragraphs: [
      "The single most common message we get from members after their first Matchmaker dinner is a variation of the same thing: \u201CI wasn't expecting it to actually work.\u201D That's the point. Speed-dating trained a generation of people to expect awkwardness from any social event with strangers. Matchmaker was engineered from the opposite direction — as an easy, deliberate, high-signal room.",
      "The second most common message is that people ended up staying at the venue for an hour after the dinner officially ended. The third most common is that at least two members exchanged phone numbers. About one-in-three tables end up requesting a follow-up Circle. About one-in-five members end up making a friendship that lasts more than a year. Those are unusually high numbers for any social platform — and they exist because the seating math is doing quiet work every fortnight.",
    ],
  },
];

export default function HustleMeetMatchmakerSalesPage() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    partner_venue_count: null, partner_cities: null,
    follow_up_rate_pct: null, dinners_run: null, active_members: null,
  });

  const HM_NAV = [
    { to: "/meet",             label: hm(t, "nav.hustlemeet") },
    { to: "/circles",     label: hm(t, "nav.circles") },
    { to: "/matchmaker",  label: hm(t, "nav.matchmaker") },
    { to: "/verified-members",       label: hm(t, "nav.verified") },
  ];

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = hm(t, "matchmaker.page_title");
    (async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
        const { data } = await axios.get(`${API}/hustlemeet-matchmaker/public-stats`);
        setStats((s) => ({ ...s, ...data }));
      } catch (_) { /* keep zero-state fallback */ }
    })();
  }, [t, i18n.language]);

  const heroTitle = hm(t, "matchmaker.hero_title");
  const heroAccent = hm(t, "matchmaker.hero_title_accent");
  const heroParts = heroTitle.split(heroAccent);

  return (
    <div
      data-testid="hustlemeet-matchmaker-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: hm(t, "nav.hustlemeet"), to: "/meet" }}
        nav={HM_NAV}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section data-testid="matchmaker-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={{ background: "linear-gradient(135deg,#0a0908 0%,#1c1917 55%,#7c2d12 120%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.kicker")}
          </p>
          <h1 className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl">
            {heroParts.length === 2 ? (
              <>{heroParts[0]}<span className="text-amber-400">{heroAccent}</span>{heroParts[1]}</>
            ) : heroTitle}
          </h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl">{hm(t, "matchmaker.hero_sub")}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild
              className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black h-12 px-6"
              data-testid="cta-join-membership"
            >
              <a href="https://app.hustlemeetups.au/signup" rel="noopener">
                {hm(t, "matchmaker.cta_join") || "Join Hustle Membership"}
              </a>
            </Button>
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-take-quiz"
            >
              <Link to="/meet/profile">{hm(t, "matchmaker.cta_quiz")}</Link>
            </Button>
            <Button asChild variant="outline"
              className="rounded-full border-amber-400 text-amber-300 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="cta-hero-see-circles"
            >
              <Link to="/circles">{hm(t, "matchmaker.cta_see_circles")}</Link>
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl text-white/70 text-xs">
            {[
              [Brain, "matchmaker.trust_big5"],
              [Users, "matchmaker.trust_seats"],
              [Lock,  "matchmaker.trust_lock"],
              [Shield,"matchmaker.trust_verified"],
            ].map(([I, k], i) => (
              <div key={i} className="inline-flex items-center gap-2">
                <I size={14} className="text-amber-400"/> {hm(t, k)}
              </div>
            ))}
          </div>
          <p data-testid="mm-anonymous-note"
             className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-full px-4 py-2">
            <Lock size={14} className="text-amber-300"/>
            Table group meeting is anonymous and has no Group Host.
          </p>

          {/* Live proof stats (Q7a — replace hardcoded numbers) */}
          <div
            data-testid="mm-live-proof"
            className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl"
          >
            {[
              ["Partner venues", stats.partner_venue_count],
              ["Cities live",    stats.partner_cities],
              ["Dinners run",    stats.dinners_run],
              ["Active members", stats.active_members],
            ].map(([label, value], i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 dark:bg-stone-900">
                <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-black">{label}</p>
                <p className="mt-1 text-lg font-black text-white">
                  {value === null || value === undefined ? "—" : Number(value).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">

        {/* How Matchmaker works */}
        <section data-testid="mm-how-it-works">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.how_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.how_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[[Brain, 1], [Zap, 2], [Heart, 3], [Repeat, 4]].map(([Ico, n]) => (
              <div key={n} data-testid={`mm-step-${n}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                  {hm(t, `landing.how_step_num`, { defaultValue: `Step ${n}` }).replace(/\{n\}/, n)}
                </p>
                <p className="mt-1 font-black">{hm(t, `matchmaker.how_step${n}_ttl`)}</p>
                <p className="mt-1 text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `matchmaker.how_step${n}_sub`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* The science */}
        <section data-testid="mm-science"
          className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-12"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.science_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.science_title")}</h2>
          <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 max-w-2xl">{hm(t, "matchmaker.science_sub")}</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[[Brain, 1], [Heart, 2], [Sparkles, 3], [Clock, 4]].map(([Ico, n]) => (
              <div key={n} className="flex gap-4">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <div>
                  <p className="font-black">{hm(t, `matchmaker.science_${n}_ttl`)}</p>
                  <p className="mt-1 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                    {hm(t, `matchmaker.science_${n}_body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Safety commitments */}
        <section data-testid="mm-safety">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.safety_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.safety_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              [Shield, 1], [Lock, 2], [Users, 3],
              [MessageSquare, 4], [MapPin, 5], [Star, 6],
            ].map(([Ico, n]) => (
              <div key={n}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 font-black">{hm(t, `matchmaker.safety_${n}_ttl`)}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `matchmaker.safety_${n}_body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Learning loop */}
        <section data-testid="mm-learning-loop"
          className="rounded-3xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-8 md:p-12"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.loop_kicker")}
          </p>
          <h2 className="mt-2 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.loop_title")}</h2>
          <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-2xl">{hm(t, "matchmaker.loop_body")}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li key={n} className="inline-flex items-start gap-2 text-sm">
                <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                <span>{hm(t, `matchmaker.loop_${n}`)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Circles vs Matchmaker */}
        <section data-testid="mm-vs-circles">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "matchmaker.vs_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.vs_title")}</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-testid="compare-matchmaker"
              className="rounded-2xl border-2 border-amber-400 bg-white dark:bg-slate-950 p-6"
            >
              <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                {hm(t, "nav.matchmaker")}
              </p>
              <p className="mt-2 font-black text-lg">{hm(t, "matchmaker.vs_mm_ttl")}</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700 dark:text-stone-300">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="inline-flex gap-2">
                    <CheckCircle2 size={14} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                    {hm(t, `matchmaker.vs_mm_${n}`)}
                  </li>
                ))}
              </ul>
              <Button asChild
                className="mt-6 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black w-full"
                data-testid="cta-compare-take-quiz"
              >
                <Link to="/meet/profile">{hm(t, "matchmaker.vs_mm_cta")}</Link>
              </Button>
            </div>
            <div data-testid="compare-circles"
              className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6"
            >
              <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                {hm(t, "nav.circles")}
              </p>
              <p className="mt-2 font-black text-lg">{hm(t, "matchmaker.vs_circles_ttl")}</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700 dark:text-stone-300">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="inline-flex gap-2">
                    <CheckCircle2 size={14} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                    {hm(t, `matchmaker.vs_circles_${n}`)}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline"
                className="mt-6 rounded-full border-amber-400 text-amber-500 hover:bg-amber-500/10 font-black w-full"
                data-testid="cta-compare-see-circles"
              >
                <Link to="/circles">{hm(t, "matchmaker.vs_circles_cta")}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* ── Long-form sales article (English source-of-truth). */}
        <article
          data-testid="mm-sales-article"
          className="border-t border-stone-200 dark:border-stone-800 pt-12"
        >
          <figure
            data-testid="mm-hero-figure"
            className="rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm mb-12"
          >
            <img
              src={MM_HERO_IMG}
              alt="Six warm strangers around a candle-lit restaurant table — a Matchmaker dinner mid-conversation"
              loading="lazy"
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
            />
            <figcaption className="text-[11px] text-stone-500 dark:text-stone-400 px-4 py-3 bg-stone-50 dark:bg-stone-900/50">
              A Matchmaker table mid-night — six strangers who feel like old friends by dessert.
            </figcaption>
          </figure>

          <p
            className="text-2xl sm:text-3xl font-black leading-snug text-stone-900 dark:text-stone-50 max-w-3xl"
            data-testid="mm-sales-lead"
          >
            Six Strangers. One Table. <span className="text-amber-500">One Great Night.</span>
          </p>
          <div className="max-w-3xl">
            {MM_INTRO_PARAS.map((para, i) => (
              <p
                key={`mm-intro-${i}`}
                className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
              >
                {para}
              </p>
            ))}
          </div>

          {MM_SECTIONS.map((s, i) => (
            <section
              key={`mm-sec-${i}`}
              data-testid={`mm-sales-section-${i}`}
              className="mt-12 max-w-3xl"
            >
              <h2 className="text-2xl sm:text-3xl font-black leading-tight text-stone-900 dark:text-stone-50">
                {s.title}
              </h2>
              {s.paragraphs.map((para, pi) => (
                <p
                  key={`mm-p-${pi}`}
                  className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
                >
                  {para}
                </p>
              ))}
              {i === 2 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={MM_IMG_SCIENCE}
                    alt="A wooden table with a notebook and coffee — the science behind Matchmaker seating"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
              {i === 5 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={MM_IMG_TOAST}
                    alt="Friends toasting with wine glasses — the moment a Matchmaker table clicks"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
              {i === 8 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={MM_IMG_CANDLES}
                    alt="A candle-lit place setting waiting for a Matchmaker guest"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
            </section>
          ))}

          <div
            data-testid="mm-sales-signature"
            className="mt-14 pt-8 border-t border-stone-200 dark:border-stone-800 max-w-3xl"
          >
            <p className="text-lg sm:text-xl font-black text-stone-900 dark:text-stone-50">
              Matchmaker
            </p>
            <p className="mt-1 text-lg sm:text-xl font-bold text-stone-800 dark:text-stone-100">
              One Quiz. Countless Nights.{" "}
              <span className="text-amber-500">Six New Faces Every Time.</span>
            </p>
          </div>
        </article>

        {/* Bottom CTA */}
        <section data-testid="mm-bottom-cta"
          className="text-center rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-12"
        >
          <Sparkles size={28} className="mx-auto text-amber-500" aria-label="Matchmaker" title="Matchmaker"/>
          <h2 className="mt-4 text-2xl md:text-4xl font-black">{hm(t, "matchmaker.bottom_title")}</h2>
          <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-2xl mx-auto">
            {hm(t, "matchmaker.bottom_sub")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-bottom-take-quiz"
            >
              <Link to="/meet/profile">
                {hm(t, "matchmaker.bottom_cta")} <ArrowRight size={14} className="ml-1" aria-hidden="true"/>
              </Link>
            </Button>
          </div>
          <p className="mt-4 text-[10px] text-stone-600 dark:text-stone-300 uppercase tracking-widest">
            {hm(t, "matchmaker.bottom_note")}
          </p>
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
